import React from "react";
import { IndianRupee, ClipboardCheck, ClipboardList, Footprints, Home, ArrowRightLeft, FileText, Settings2, UserCog, ShieldCheck, Camera } from "lucide-react";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { StaffAvatar, EmpCode } from "./StaffAvatar";
import { fmtDateTime } from "../lib/api";

/**
 * Phase 90 — shared who / what / when / why timeline used by the property "Audit trail" tab,
 * the per-staff Activity sheet and (in compact form) elsewhere.
 * Each event: {event, label, at, actor{id,name,role_label,employee_id,designation,is_active,deleted},
 *              summary, rationale, pin, property_id, ref_type, ref_number}
 */
const CATEGORY_META = {
  payment: { icon: IndianRupee, cls: "bg-emerald-100 text-emerald-700" },
  reassessment: { icon: ClipboardList, cls: "bg-sky-100 text-sky-700" },
  survey: { icon: FileText, cls: "bg-indigo-100 text-indigo-700" },
  transfer: { icon: ArrowRightLeft, cls: "bg-violet-100 text-violet-700" },
  field: { icon: Footprints, cls: "bg-amber-100 text-amber-800" },
  property: { icon: Home, cls: "bg-teal-100 text-teal-700" },
  cheque: { icon: IndianRupee, cls: "bg-lime-100 text-lime-800" },
  user: { icon: UserCog, cls: "bg-slate-200 text-slate-700" },
  settings: { icon: Settings2, cls: "bg-slate-200 text-slate-700" },
  approval: { icon: ClipboardCheck, cls: "bg-emerald-100 text-emerald-700" },
  default: { icon: ShieldCheck, cls: "bg-muted text-muted-foreground" },
};

export const categoryOf = (event = "") => {
  const key = event.split(".")[0];
  if (event.includes("photo")) return "property";
  return CATEGORY_META[key] ? key : "default";
};

const decisionTone = (event = "") => {
  if (event.endsWith(".approved")) return "text-emerald-700";
  if (event.endsWith(".rejected")) return "text-red-700";
  return "";
};

export const ActorLine = ({ actor, code, showAvatar = true, className = "" }) => {
  if (!actor) return <span className="text-muted-foreground">System</span>;
  const role = actor.role_label || actor.role;
  const designation = actor.designation && actor.designation.trim().toLowerCase() !== (role || "").trim().toLowerCase() ? actor.designation : null;
  const meta = [role, designation].filter(Boolean).join(" • ");
  return (
    <span className={`inline-flex items-center gap-1.5 min-w-0 ${className}`} data-testid="audit-actor">
      {showAvatar && <StaffAvatar code={code} userId={actor.id} name={actor.name} photoUrl={actor.photo_url} size="xs" />}
      <span className="font-medium truncate">{actor.name || "Unknown"}{actor.name && !actor.platform && actor.role !== "citizen" && actor.role !== "super_admin" ? <EmpCode code={actor.employee_id} /> : null}</span>
      {meta && <span className="text-muted-foreground truncate">({meta})</span>}
      {actor.platform && <Badge variant="outline" className="text-[10px] py-0">Platform</Badge>}
      {actor.deleted ? (
        <Badge variant="outline" className="text-[10px] py-0 border-slate-300 text-slate-600" title="User record no longer exists — identity kept from the audit snapshot">Former staff</Badge>
      ) : actor.is_active === false ? (
        <Badge variant="outline" className="text-[10px] py-0 border-amber-300 bg-amber-50 text-amber-800" data-testid="audit-actor-deactivated">Deactivated</Badge>
      ) : null}
    </span>
  );
};

export const AuditTimeline = ({ code, events, loading, emptyText = "No recorded activity yet.", showPin = false, compact = false }) => {
  if (loading) {
    return (
      <div className="space-y-3" data-testid="audit-timeline-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-7 w-7 rounded-full" />
            <div className="flex-1 space-y-1.5"><Skeleton className="h-3.5 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
          </div>
        ))}
      </div>
    );
  }
  if (!events || events.length === 0) {
    return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="audit-timeline-empty">{emptyText}</p>;
  }
  return (
    <ol className="relative border-l border-border ml-3.5 space-y-4" data-testid="audit-timeline">
      {events.map((e, i) => {
        const cat = categoryOf(e.event);
        const Icon = (CATEGORY_META[cat] || CATEGORY_META.default).icon;
        const cls = (CATEGORY_META[cat] || CATEGORY_META.default).cls;
        const IconEl = e.event?.includes("photo") ? Camera : Icon;
        return (
          <li key={`${e.event}-${e.ref_id || i}-${e.at}`} className="ml-5 relative" data-testid={`audit-event-${e.event}`} data-event={e.event}>
            <span className={`absolute -left-[31px] top-0 h-7 w-7 rounded-full flex items-center justify-center ring-4 ring-background ${cls}`}>
              <IconEl className="h-3.5 w-3.5" />
            </span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
              <p className={`text-sm font-medium ${decisionTone(e.event)}`}>{e.label}</p>
              <time className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap" dateTime={e.at || undefined} data-testid="audit-event-when">
                {fmtDateTime(e.at)}
              </time>
            </div>
            {e.summary && e.summary !== e.label && (
              <p className="text-xs text-foreground/90 mt-0.5 break-words" data-testid="audit-event-summary">{e.summary}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1.5">
              <span>By</span> <ActorLine actor={e.actor} code={code} />
              {showPin && e.pin && <><span>•</span><span className="font-mono">{e.pin}</span></>}
              {!compact && e.ref_number && <><span>•</span><span className="font-mono">{e.ref_number}</span></>}
            </p>
            {e.rationale && (
              <p className="mt-1.5 text-xs rounded-md border border-border bg-muted/40 px-2.5 py-1.5" data-testid="audit-event-rationale">
                <span className="text-muted-foreground">Rationale:</span> {e.rationale}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};
